import axios from 'axios';
import { API_BASE_URL } from '@/constants';

// In-Memory Token Store
let inMemoryAccessToken = null;

/**
 * Check if a JWT token is expired using its standard exp claim
 */
export const isTokenExpired = (token) => {
  if (!token || typeof token !== 'string') return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson);
    if (!payload.exp) return false;
    // Buffer of 10 seconds before actual expiration
    return Date.now() >= payload.exp * 1000 - 10000;
  } catch (e) {
    return true;
  }
};

export const setAuthToken = (token) => {
  inMemoryAccessToken = token;
  if (token) {
    localStorage.setItem('pms_token', token);
  } else {
    localStorage.removeItem('pms_token');
  }
};

export const getAuthToken = () => {
  const token = inMemoryAccessToken || localStorage.getItem('pms_token');
  if (token && isTokenExpired(token)) {
    clearAuthToken();
    return null;
  }
  return token;
};

export const clearAuthToken = () => {
  inMemoryAccessToken = null;
  localStorage.removeItem('pms_token');
  localStorage.removeItem('pms_user');
};

// Helper to redirect to login while preserving hospital context
const redirectToLogin = () => {
  const isAuthPage = window.location.pathname.includes('/login');
  if (isAuthPage) return;

  let targetUrl = '/login';
  try {
    const savedHosp = localStorage.getItem('pulseflow_pms_hospital');
    if (savedHosp) {
      const parsed = JSON.parse(savedHosp);
      if (parsed?.id) {
        targetUrl = `/login?hospitalId=${parsed.id}`;
      }
    }
  } catch (e) {
    // fallback to /login
  }

  window.location.href = targetUrl;
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request Interceptor: Attach Bearer token if valid
apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response Interceptor: Handle API response and handle 401 with clean redirect
apiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Skip redirect logic for auth endpoints so login errors are handled by form
    const isAuthEndpoint =
      originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/forgot-password') ||
      originalRequest?.url?.includes('/auth/reset-password');

    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    // When 401 occurs on any protected API request, clear dead token and redirect
    if (error.response?.status === 401) {
      clearAuthToken();
      redirectToLogin();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
